import { useSiteSettings } from "@/hooks/use-site-settings";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import QuestionCard from "@/components/QuestionCard";
import FiltersBar from "@/components/FiltersBar";
import { Loader2, SearchX } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { KineticText, Reveal, TiltCard, EASE } from "@/components/motion/primitives";
import Seo from "@/components/Seo";

const DEFAULTS = { search: "", testament: "all", who_group: "all", theme: "all", type: "all", book: "all" };
const PAGE = 36;

export default function Explore() {
  const exploreThemes = useSiteSettings().settings.explore_themes;
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    ...DEFAULTS,
    search: searchParams.get("q") || "",
    who_group: searchParams.get("who") || "all",
    book: searchParams.get("book") || "all",
    theme: searchParams.get("theme") || "all",
    testament: searchParams.get("testament") || "all",
  });
  const [debounced, setDebounced] = useState(filters.search);
  const [compact, setCompact] = useState(false);
  useEffect(() => {
    const onScroll = () => setCompact(window.scrollY > 140);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q !== null) setFilters((f) => (f.search === q ? f : { ...f, search: q }));
  }, [searchParams]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(filters.search), 250);
    return () => clearTimeout(t);
  }, [filters.search]);

  const { data: meta } = useQuery({
    queryKey: ["meta"],
    queryFn: async () => (await api.get("/questions/meta")).data,
  });

  const params = useMemo(
    () => ({
      search: debounced || undefined,
      testament: filters.testament,
      who_group: filters.who_group,
      theme: filters.theme,
      type: filters.type,
      book: filters.book,
    }),
    [debounced, filters.testament, filters.who_group, filters.theme, filters.type, filters.book]
  );

  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["questions", params],
    queryFn: async ({ pageParam = 0 }) => (await api.get("/questions", { params: { ...params, limit: PAGE, offset: pageParam } })).data,
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.questions.length, 0);
      return loaded < last.count ? loaded : undefined;
    },
    placeholderData: (prev) => prev,
  });

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const resetFilters = () => setFilters(DEFAULTS);

  const questions = useMemo(() => data?.pages.flatMap((p) => p.questions) || [], [data]);
  const count = data?.pages[0]?.count;
  const terms = data?.pages[0]?.terms || [];

  return (
    <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
      <header className="mb-8">
      <Seo title={debounced ? `Search: ${debounced}` : "Explore the Questions of the Bible"} description="Search and filter every question asked in Scripture by book, speaker, type of question, and theme." />
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">Scripture mosaic</span>
        <KineticText as="h1" text="Explore the questions" className="mt-2 block font-serif text-4xl font-bold leading-[1.05] tracking-tight text-foreground sm:text-5xl" testId="explore-headline" />
        <Reveal delay={0.3}>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Filter by testament, the voice who asked, {exploreThemes ? "the theme, " : ""}or the type of question. Every one is a
            door into deeper conversation.
          </p>
        </Reveal>
      </header>

      <div className={`sticky top-16 z-30 -mx-4 mb-8 border-b border-border bg-background/85 px-4 glass transition-[padding] duration-200 sm:mx-0 sm:rounded-2xl sm:border sm:px-6 ${compact ? "py-2" : "py-2 md:py-4"}`} data-testid="filters-sticky">
        <FiltersBar
          meta={meta}
          filters={filters}
          setFilter={setFilter}
          resetFilters={resetFilters}
          resultCount={count}
          compact={compact}
        />
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={`skeleton-${i}`} className="h-56 animate-pulse rounded-xl bg-muted" />
          ))}
        </div>
      ) : questions.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-border py-24 text-center">
          <SearchX className="h-10 w-10 text-muted-foreground" />
          <p className="mt-4 font-serif text-2xl text-foreground">No questions found</p>
          <p className="mt-1 text-sm text-muted-foreground">Try clearing a filter or searching differently.</p>
        </div>
      ) : (
        <>
          <motion.div layout className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3" data-testid="results-grid">
            <AnimatePresence mode="popLayout" initial={false}>
              {questions.map((q, i) => (
                <motion.div
                  key={q.id}
                  layout
                  initial={{ opacity: 0, y: 24, scale: 0.97 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.96, transition: { duration: 0.18 } }}
                  transition={{ duration: 0.5, ease: EASE, delay: Math.min(i % PAGE, 8) * 0.04 }}
                >
                  <TiltCard max={6} className="group h-full rounded-xl">
                    <QuestionCard q={q} index={i} terms={terms} />
                  </TiltCard>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>
          {hasNextPage && (
            <div className="mt-10 flex flex-col items-center gap-3">
              <p className="font-mono text-xs uppercase tracking-wider text-muted-foreground" data-testid="results-progress">
                Showing {questions.length} of {count}
              </p>
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:border-gold hover:bg-gold/10 disabled:opacity-60"
                data-testid="load-more-btn"
              >
                {isFetchingNextPage && <Loader2 className="h-4 w-4 animate-spin" />}
                {isFetchingNextPage ? "Loading…" : "Load more questions"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
